import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  ShipmentPaymentOptionSchema,
  SpecialRateEligibility,
} from "../outbound-labels/outbound-labels";
import { OutboundParcelSchema } from "../outbound-labels/parcel";
import { WeightSchema } from "../outbound-labels/util";
import { TagSchema } from "../tag";
import { RedoShopifyDeliveryMethodType } from "./fulfillment-delivery-method-type";
import { GroupSelectedRateSchema } from "./fulfillment-group";
import { PrintStatusSchema } from "./fulfillment-print-status";
import { Hazmat } from "./hazmat";
import { OMSUserSchema } from "./oms-user";
import {
  ItemParcelAssignmentsSchema,
  PackingParcelsSchema,
} from "./packing-parcels";

export const FulfillmentGroupChangesSchema = z.discriminatedUnion("field", [
  z.object({
    field: z.literal("selectedShippingRate"),
    prev: GroupSelectedRateSchema.nullable(),
    next: GroupSelectedRateSchema.nullable(),
  }),
  z.object({
    field: z.literal("signatureRequired"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("adultSignatureRequired"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("specialRateEligibility"),
    prev: createEnum(SpecialRateEligibility).nullable(),
    next: createEnum(SpecialRateEligibility).nullable(),
  }),
  z.object({
    field: z.literal("selectedParcels"),
    prev: z.array(OutboundParcelSchema).nullable(),
    next: z.array(OutboundParcelSchema).nullable(),
  }),
  z.object({
    field: z.literal("hazmat"),
    prev: createEnum(Hazmat).nullable(),
    next: createEnum(Hazmat).nullable(),
  }),
  z.object({
    field: z.literal("dryIceWeight"),
    prev: WeightSchema.nullable(),
    next: WeightSchema.nullable(),
  }),
  z.object({
    field: z.literal("saturdayDelivery"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("buyReturnLabel"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("ddp"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("overrideDeliveryMethod"),
    prev: createEnum(RedoShopifyDeliveryMethodType).nullable(),
    next: createEnum(RedoShopifyDeliveryMethodType).nullable(),
  }),
  z.object({
    field: z.literal("assignedUser"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
  z.object({
    field: z.literal("tags"),
    prev: z.array(TagSchema).nullable(),
    next: z.array(TagSchema).nullable(),
  }),
  z.object({
    field: z.literal("noteToBuyer"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
  z.object({
    field: z.literal("internalNote"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
  z.object({
    field: z.literal("printStatus"),
    prev: PrintStatusSchema.nullable(),
    next: PrintStatusSchema.nullable(),
  }),
  z.object({
    field: z.literal("scanVerified"),
    prev: z
      .object({
        verifiedBy: OMSUserSchema.nullish(),
        verifiedAt: z.date().nullish(),
      })
      .nullable(),
    next: z
      .object({
        verifiedBy: OMSUserSchema.nullish(),
        verifiedAt: z.date().nullish(),
      })
      .nullable(),
  }),
  z.object({
    field: z.literal("packingSlipTemplateId"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
  z.object({
    field: z.literal("packingParcels"),
    prev: PackingParcelsSchema.nullable(),
    next: PackingParcelsSchema.nullable(),
  }),
  z.object({
    field: z.literal("itemParcelAssignments"),
    prev: ItemParcelAssignmentsSchema.nullable(),
    next: ItemParcelAssignmentsSchema.nullable(),
  }),
  z.object({
    field: z.literal("parcelsFinalized"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("shipmentPaymentOption"),
    prev: ShipmentPaymentOptionSchema.nullable(),
    next: ShipmentPaymentOptionSchema.nullable(),
  }),
  z.object({
    field: z.literal("carbonNeutral"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("directDeliveryOnly"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("overrideResidentialAddress"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("assignedFulfillmentService"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
]);
export type FulfillmentGroupChanges = z.infer<
  typeof FulfillmentGroupChangesSchema
>;

export const FulfillmentGroupUpdateSource = { SCALE: "scale" } as const;
export type FulfillmentGroupUpdateSource =
  (typeof FulfillmentGroupUpdateSource)[keyof typeof FulfillmentGroupUpdateSource];

const fulfillmentGroupAuditSchema = z.object({
  id: z.bigint().optional(),
  accountId: z.bigint(),
  fulfillmentGroupId: z.string(),
  teamId: z.string(),
  userId: z.string(),
  source: createEnum(FulfillmentGroupUpdateSource).nullish(),
  changes: z.array(FulfillmentGroupChangesSchema),
  createdAt: z.date(),
});
export const FulfillmentGroupAuditSchema: z.ZodType<FulfillmentGroupAudit> =
  fulfillmentGroupAuditSchema;
export type FulfillmentGroupAudit = z.infer<typeof fulfillmentGroupAuditSchema>;
